import React, { useState, useEffect } from "react";
import {
  TextInput,
  NumberInput,
  Textarea,
  Combobox,
  Group,
  Button,
  Stack,
  Text,
  Image,
  Box,
  Divider,
  Title,
  InputBase,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { IconBarcode, IconX, IconInfoCircle } from "@tabler/icons-react";
import { useProducts } from "../../context/ProductsContext";
import {
  getUniqueCategories,
  getUniqueCompanies,
} from "../../utils/productSearchUtils";

export default function ProductForm({
  product,
  onSubmit,
  onCancel,
  isSubmitting = false,
  mode = "add", // 'add' or 'edit'
  onModalTitleChange,
  readOnly = false,
}) {
  const { products } = useProducts();
  const [currentStep, setCurrentStep] = useState(1);
  const [productCode, setProductCode] = useState("");
  const [productCodeError, setProductCodeError] = useState("");
  const [formData, setFormData] = useState({
    custom_id: "",
    name: "",
    category: "",
    company: "",
    purchase_price: "",
    list_price: "",
    description: "",
    img_url: "",
    quantity: "",
    reorder_threshold: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});

  // Barcode scanner detection
  const [barcodeBuffer, setBarcodeBuffer] = useState("");
  const [lastKeyTime, setLastKeyTime] = useState(0);

  // Dropdown management state
  const [categoryOpened, setCategoryOpened] = useState(false);
  const [categorySearchQuery, setCategorySearchQuery] = useState("");
  const [categoryFocused, setCategoryFocused] = useState(false);
  const [companyOpened, setCompanyOpened] = useState(false);
  const [companySearchQuery, setCompanySearchQuery] = useState("");
  const [companyFocused, setCompanyFocused] = useState(false);

  const existingCategories = getUniqueCategories(products);
  const existingCompanies = getUniqueCompanies(products);

  useEffect(() => {
    if (product && mode === "edit") {
      setProductCode(product.custom_id || "");
      setFormData({
        custom_id: product.custom_id || "",
        name: product.name || "",
        category: product.category || "",
        company: product.company || "",
        purchase_price: product.purchase_price?.toString() || "",
        list_price: product.list_price?.toString() || "",
        description: product.description || "",
        img_url: product.img_url || "",
        quantity: product.quantity?.toString() || "",
        reorder_threshold: product.reorder_threshold?.toString() || "",
        notes: product.notes || "",
      });
      // Initialize search queries for edit mode
      setCategorySearchQuery(product.category || "");
      setCompanySearchQuery(product.company || "");
      // Skip to second step for edit mode
      setCurrentStep(2);
    } else if (onModalTitleChange) {
      onModalTitleChange("Product Identification");
    }
  }, [product, mode, onModalTitleChange]);

  // Barcode scanner detection effect
  useEffect(() => {
    const handleKeyDown = (event) => {
      const currentTime = Date.now();

      // If it's been more than 100ms since last key, reset buffer (new scan)
      if (currentTime - lastKeyTime > 100) {
        setBarcodeBuffer("");
      }

      setLastKeyTime(currentTime);

      // Handle Enter key (end of barcode scan)
      if (event.key === "Enter") {
        if (barcodeBuffer.length > 0) {
          // Set the scanned barcode to the product code field
          setProductCode(barcodeBuffer);
          setProductCodeError(""); // Clear any existing errors
          setBarcodeBuffer(""); // Reset buffer
          event.preventDefault(); // Prevent form submission
        }
        return;
      }

      // Only capture printable characters
      if (event.key.length === 1 && event.key.charCodeAt(0) >= 32) {
        setBarcodeBuffer((prev) => prev + event.key);
      }
    };

    // Only add listener when on first step and in add mode
    if (currentStep === 1 && mode === "add") {
      document.addEventListener("keydown", handleKeyDown);

      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [currentStep, mode, barcodeBuffer, lastKeyTime, onModalTitleChange]);

  const validateProductCode = (code) => {
    if (!code.trim()) {
      return "Product code is required";
    }

    if (mode === "add") {
      const existingProduct = products.find((p) => p.custom_id === code.trim());
      if (existingProduct) {
        return `Product already exists: ${existingProduct.name} (${existingProduct.category}) by ${existingProduct.company}`;
      }
    } else if (mode === "edit") {
      const existingProduct = products.find(
        (p) => p.custom_id === code.trim() && p.id !== product.id,
      );
      if (existingProduct) {
        return `Product already exists: ${existingProduct.name} (${existingProduct.category}) by ${existingProduct.company}`;
      }
    }

    return null;
  };

  const handleProductCodeChange = (value) => {
    setProductCode(value);
    setProductCodeError("");
  };

  const handleProductCodeBlur = () => {
    const error = validateProductCode(productCode);
    setProductCodeError(error);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  // Category field handlers
  const handleCategoryFocus = () => {
    setCategoryFocused(true);
  };

  const handleCategoryBlur = () => {
    // Preserve custom value even if not in existing categories
    if (
      categorySearchQuery.trim() &&
      !existingCategories.includes(categorySearchQuery.trim())
    ) {
      handleInputChange("category", categorySearchQuery.trim());
    }
    setCategoryFocused(false);
    setCategoryOpened(false);
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    setCategorySearchQuery(value);
    handleInputChange("category", value);
    setCategoryOpened(true);
  };

  const handleCategoryOptionSelect = (value) => {
    handleInputChange("category", value);
    setCategorySearchQuery(value);
    setCategoryOpened(false);
    setCategoryFocused(false);
  };

  // Company field handlers
  const handleCompanyFocus = () => {
    setCompanyFocused(true);
  };

  const handleCompanyBlur = () => {
    // Preserve custom value even if not in existing companies
    if (
      companySearchQuery.trim() &&
      !existingCompanies.includes(companySearchQuery.trim())
    ) {
      handleInputChange("company", companySearchQuery.trim());
    }
    setCompanyFocused(false);
    setCompanyOpened(false);
  };

  const handleCompanyChange = (e) => {
    const value = e.target.value;
    setCompanySearchQuery(value);
    handleInputChange("company", value);
    setCompanyOpened(true);
  };

  const handleCompanyOptionSelect = (value) => {
    handleInputChange("company", value);
    setCompanySearchQuery(value);
    setCompanyOpened(false);
    setCompanyFocused(false);
  };

  const goToNextStep = () => {
    const error = validateProductCode(productCode);
    if (error) {
      setProductCodeError(error);
      return;
    }

    setFormData((prev) => ({ ...prev, custom_id: productCode.trim() }));
    setCurrentStep(2);
    onModalTitleChange("Product Information");
  };

  const goToPreviousStep = () => {
    setCurrentStep(1);
    onModalTitleChange("Product Identification");
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.custom_id.trim()) {
      newErrors.custom_id = "Custom ID is required";
    }
    if (!formData.name.trim()) {
      newErrors.name = "Product name is required";
    }
    if (!formData.category.trim()) {
      newErrors.category = "Category is required";
    }
    if (!formData.company.trim()) {
      newErrors.company = "Company is required";
    }
    if (formData.list_price && parseFloat(formData.list_price) < 0) {
      newErrors.list_price = "List price must be a positive number";
    }
    if (formData.purchase_price && parseFloat(formData.purchase_price) < 0) {
      newErrors.purchase_price = "Purchase price must be a positive number";
    }
    // Only validate quantity in add mode
    if (mode === "add") {
      if (!formData.quantity || parseInt(formData.quantity) < 0) {
        newErrors.quantity = "Quantity must be a positive number";
      }
    }
    if (
      !formData.reorder_threshold ||
      parseInt(formData.reorder_threshold) < 0
    ) {
      newErrors.reorder_threshold =
        "Reorder threshold must be a positive number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData = {
      custom_id: formData.custom_id.trim(),
      name: formData.name.trim(),
      category: formData.category.trim(),
      company: formData.company.trim(),
      purchase_price: formData.purchase_price
        ? parseFloat(formData.purchase_price)
        : null,
      list_price: parseFloat(formData.list_price),
      description: formData.description.trim(),
      img_url: formData.img_url.trim(),
      reorder_threshold: parseInt(formData.reorder_threshold),
      notes: formData.notes.trim(),
    };

    // Only include quantity when adding a new product
    if (mode === "add") {
      submitData.initial_quantity = parseInt(formData.quantity);
    }

    onSubmit(submitData);
  };

  // First Screen - Product Code Entry
  if (currentStep === 1 && mode === "add") {
    return (
      <>
        <Stack gap="xl" align="center" p="md">
          <Stack align="center" gap="xs">
            <Stack align="center" mb="md" style={{ position: "relative" }}>
              <IconBarcode
                size={80}
                stroke={1.5}
                style={{
                  color: "var(--color-primary)",
                }}
              />
            </Stack>
            <Title order={3} ta="center">
              Scan Your Product
            </Title>
            <Text size="sm" c="dimmed" ta="center">
              Use an in-store scanner to scan the product's barcode.
            </Text>
          </Stack>

          <Divider
            label="or"
            labelPosition="center"
            style={{ width: "100%", maxWidth: "400px" }}
          />

          <Stack align="center" gap="xs" w="100%">
            <TextInput
              label="Enter a unique product code"
              floating
              placeholder="e.g., PROD-001"
              value={productCode}
              onChange={(e) => handleProductCodeChange(e.target.value)}
              onBlur={handleProductCodeBlur}
              error={productCodeError}
              required
              style={{ width: "100%", maxWidth: "500px" }}
            />
          </Stack>

          <Group justify="center" mt="xs" gap="xl">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={goToNextStep} disabled={!productCode.trim()}>
              Continue
            </Button>
          </Group>
        </Stack>
      </>
    );
  }

  // Second Screen - Product Details Form
  const isDisabled = isSubmitting || readOnly;

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        <Group grow>
          <TextInput
            label={
              mode !== "edit" ? (
                "Product Code"
              ) : (
                <Text span fw={450} c="#666">
                  Product Code
                  <Tooltip label="Must be unique across all products">
                    <ActionIcon variant="subtle" size="sm">
                      <IconInfoCircle size={14} />
                    </ActionIcon>
                  </Tooltip>
                </Text>
              )
            }
            placeholder="e.g., PROD-001"
            value={mode === "edit" ? formData.custom_id : productCode}
            onChange={(e) => {
              if (mode === "edit") {
                handleInputChange("custom_id", e.target.value);
              } else {
                handleProductCodeChange(e.target.value);
              }
            }}
            onBlur={
              mode === "edit"
                ? () => {
                    const error = validateProductCode(formData.custom_id);
                    if (error) {
                      setErrors((prev) => ({ ...prev, custom_id: error }));
                    } else {
                      setErrors((prev) => ({ ...prev, custom_id: null }));
                    }
                  }
                : undefined
            }
            error={mode === "edit" ? errors.custom_id : productCodeError}
            required={!readOnly}
            disabled={mode === "add" || isDisabled}
            readOnly={readOnly}
          />
          <TextInput
            label="Product Name"
            placeholder="e.g., Professional Shampoo"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            error={errors.name}
            required={!readOnly}
            disabled={isDisabled}
            readOnly={readOnly}
          />
        </Group>

        <Group grow>
          <Combobox
            opened={categoryOpened}
            onChange={setCategoryOpened}
            onOptionSubmit={handleCategoryOptionSelect}
          >
            <Combobox.Target>
              <InputBase
                label="Category"
                placeholder="Select or type category"
                value={categorySearchQuery}
                onChange={handleCategoryChange}
                onFocus={handleCategoryFocus}
                onBlur={handleCategoryBlur}
                onClick={() => setCategoryOpened(true)}
                error={errors.category}
                required
                rightSection={
                  categorySearchQuery ? (
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      onClick={() => {
                        setCategorySearchQuery("");
                        handleInputChange("category", "");
                      }}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : (
                    <Combobox.Chevron />
                  )
                }
              />
            </Combobox.Target>
            {categoryOpened && (
              <Combobox.Dropdown>
                <Combobox.Options>
                  {!categorySearchQuery ? (
                    existingCategories.map((category) => (
                      <Combobox.Option key={category} value={category}>
                        {category}
                      </Combobox.Option>
                    ))
                  ) : (
                    <>
                      {existingCategories
                        .filter((cat) =>
                          cat
                            .toLowerCase()
                            .includes(categorySearchQuery.toLowerCase()),
                        )
                        .map((category) => (
                          <Combobox.Option key={category} value={category}>
                            {category}
                          </Combobox.Option>
                        ))}
                      {!existingCategories.some((cat) =>
                        cat
                          .toLowerCase()
                          .includes(categorySearchQuery.toLowerCase()),
                      ) && (
                        <Combobox.Option value={categorySearchQuery}>
                          + Create "{categorySearchQuery}"
                        </Combobox.Option>
                      )}
                    </>
                  )}
                </Combobox.Options>
              </Combobox.Dropdown>
            )}
          </Combobox>
          <Combobox
            opened={companyOpened}
            onChange={setCompanyOpened}
            onOptionSubmit={handleCompanyOptionSelect}
          >
            <Combobox.Target>
              <InputBase
                label="Company"
                placeholder="Select or type company"
                value={companySearchQuery}
                onChange={handleCompanyChange}
                onFocus={handleCompanyFocus}
                onBlur={handleCompanyBlur}
                onClick={() => setCompanyOpened(true)}
                error={errors.company}
                required
                rightSection={
                  companySearchQuery ? (
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      onClick={() => {
                        setCompanySearchQuery("");
                        handleInputChange("company", "");
                      }}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : (
                    <Combobox.Chevron />
                  )
                }
              />
            </Combobox.Target>
            {companyOpened && (
              <Combobox.Dropdown>
                <Combobox.Options>
                  {!companySearchQuery ? (
                    existingCompanies.map((company) => (
                      <Combobox.Option key={company} value={company}>
                        {company}
                      </Combobox.Option>
                    ))
                  ) : (
                    <>
                      {existingCompanies
                        .filter((comp) =>
                          comp
                            .toLowerCase()
                            .includes(companySearchQuery.toLowerCase()),
                        )
                        .map((company) => (
                          <Combobox.Option key={company} value={company}>
                            {company}
                          </Combobox.Option>
                        ))}
                      {!existingCompanies.some((comp) =>
                        comp
                          .toLowerCase()
                          .includes(companySearchQuery.toLowerCase()),
                      ) && (
                        <Combobox.Option value={companySearchQuery}>
                          + Create "{companySearchQuery}"
                        </Combobox.Option>
                      )}
                    </>
                  )}
                </Combobox.Options>
              </Combobox.Dropdown>
            )}
          </Combobox>
        </Group>

        <Group grow>
          <NumberInput
            label="List Price ($)"
            placeholder="0.00"
            value={formData.list_price}
            onChange={(value) => handleInputChange("list_price", value)}
            error={errors.list_price}
            min={0}
            step={0.01}
            precision={2}
            required={!readOnly}
            disabled={isDisabled}
            readOnly={readOnly}
          />
          <NumberInput
            label="Purchase Price ($)"
            placeholder="0.00"
            value={formData.purchase_price}
            onChange={(value) => handleInputChange("purchase_price", value)}
            error={errors.purchase_price}
            min={0}
            step={0.01}
            precision={2}
            disabled={isDisabled}
            readOnly={readOnly}
          />
        </Group>

        <Group grow>
          {mode === "add" && (
            <NumberInput
              label="Initial Quantity"
              placeholder="0"
              value={formData.quantity}
              onChange={(value) => handleInputChange("quantity", value)}
              error={errors.quantity}
              min={0}
              required={!readOnly}
              disabled={isDisabled}
              readOnly={readOnly}
            />
          )}
          <NumberInput
            label="Reorder Threshold"
            placeholder="0"
            value={formData.reorder_threshold}
            onChange={(value) => handleInputChange("reorder_threshold", value)}
            error={errors.reorder_threshold}
            min={0}
            required={!readOnly}
            disabled={isDisabled}
            readOnly={readOnly}
            style={mode === "edit" ? { width: "100%" } : undefined}
          />
        </Group>

        <TextInput
          label="Image URL"
          placeholder="https://example.com/image.jpg"
          value={formData.img_url}
          onChange={(e) => handleInputChange("img_url", e.target.value)}
          disabled={isDisabled}
          readOnly={readOnly}
        />

        {formData.img_url && (
          <Box>
            <Text size="sm" mb="xs">
              Image Preview:
            </Text>
            <Image
              src={formData.img_url}
              alt="Product preview"
              fallbackSrc="https://placehold.co/200x200?text=No+Image"
              w={200}
              h={200}
              fit="contain"
            />
          </Box>
        )}

        <Textarea
          label="Description"
          placeholder="Product description..."
          value={formData.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
          rows={3}
          disabled={isDisabled}
          readOnly={readOnly}
        />

        <Textarea
          label="Notes"
          placeholder="Additional notes..."
          value={formData.notes}
          onChange={(e) => handleInputChange("notes", e.target.value)}
          rows={2}
          disabled={isDisabled}
          readOnly={readOnly}
        />

        <Divider />

        {!readOnly && (
          <Group justify="space-between">
            {mode === "add" && (
              <Button variant="outline" onClick={goToPreviousStep}>
                Back
              </Button>
            )}
            <Group justify="flex-end" style={{ marginLeft: "auto" }}>
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                {mode === "add" ? "Add Product" : "Update Product"}
              </Button>
            </Group>
          </Group>
        )}
        {readOnly && (
          <Group justify="flex-end">
            <Button variant="outline" onClick={onCancel}>
              Close
            </Button>
          </Group>
        )}
      </Stack>
    </form>
  );
}
